import { DATA_ASSET_KEYS } from '../assets/asset-keys.js';
import { DATA_MANAGER_STORE_KEYS, dataManager } from './data-manager.js';
import { hpAtLevel } from '../battle/type-chart.js';

// Level thresholds for the two evolution steps. Stage-1 → stage-2 at 16, stage-2 → stage-3 at 36.
// Final stages have no `toId`, so they never evolve regardless of level.
export const EVOLVE_LEVEL_STAGE_2 = 16;
export const EVOLVE_LEVEL_STAGE_3 = 36;

/**
 * Derives the evolution map from the full monster roster in the Phaser JSON cache.
 *
 * The 24 monsters form element lines: grouping by `element` and sorting by `monsterId`, the first
 * three monsters of an element are its stage-1 → stage-2 → stage-3 chain. Stage-1 evolves into
 * stage-2 at level 16, stage-2 into stage-3 at level 36. Any extra monsters in an element (a 4th+)
 * and the final stage of each line have no evolution target.
 *
 * Returned shape: `{ [fromMonsterId: number]: { toId: number, level: number } }`.
 *
 * @param {Phaser.Scene} scene the scene used to read the cached monsters.json
 * @returns {Record<number, { toId: number, level: number }>}
 */
export function buildEvolutionMap(scene) {
  /** @type {import('../types/typedef.js').Monster[]} */
  const roster = scene.cache.json.get(DATA_ASSET_KEYS.MONSTERS) || [];

  /** @type {Record<number, { toId: number, level: number }>} */
  const map = {};
  // Evolution is driven SOLELY by each monster's explicit `evolvesTo`/`evolveLevel` (mirrored from the
  // authoritative @mn/content chains). The old "first 3 monsters of an element = a chain" grouping
  // produced WRONG evolutions once apex/standalone beasts shared an element with a line (e.g. it evolved
  // Dunestalker → Grazeling, Neurolisk → Voidmaw, Boulderhide → Titanok). Apex/final beasts simply have
  // no `evolvesTo` and therefore never evolve.
  roster.forEach((m) => {
    if (typeof m.evolvesTo === 'number' && m.evolvesTo > 0) {
      map[m.monsterId] = { toId: m.evolvesTo, level: m.evolveLevel || EVOLVE_LEVEL_STAGE_2 };
    }
  });
  return map;
}

/**
 * @typedef EvolutionResult
 * @type {object}
 * @property {string} fromName the species name before evolving
 * @property {string} toName the species name after evolving
 * @property {string} fromAssetKey old sprite asset key (for the celebration overlay)
 * @property {number} fromAssetFrame old sprite frame
 * @property {number} fromAssetScale old sprite scale
 * @property {string} toAssetKey new sprite asset key
 * @property {number} toAssetFrame new sprite frame
 * @property {number} toAssetScale new sprite scale
 */

/**
 * Scans the saved party and evolves every monster that has reached its evolution level. Species
 * fields (monsterId, name, assetKey/frame/scale, base stats) are replaced with the target species'
 * values, while progression that belongs to the individual is PRESERVED: the uuid `id`, the
 * `currentLevel`, `currentExp`, and the current HP *as a ratio* (so a hurt monster stays hurt).
 *
 * Safe to call repeatedly (idempotent once a monster has no further target) and from any scene
 * OUTSIDE of battle. Persists the party and returns the list of evolutions that happened so the
 * caller can show the celebration overlay.
 *
 * @param {Phaser.Scene} scene the scene used to read the cached monsters.json
 * @returns {EvolutionResult[]} one entry per monster that evolved this pass (may be empty)
 */
export function checkAndEvolveParty(scene) {
  /** @type {import('../types/typedef.js').Monster[]} */
  const party = dataManager.store.get(DATA_MANAGER_STORE_KEYS.MONSTERS_IN_PARTY) || [];
  if (party.length === 0) {
    return [];
  }

  const evolutionMap = buildEvolutionMap(scene);
  /** @type {import('../types/typedef.js').Monster[]} */
  const roster = scene.cache.json.get(DATA_ASSET_KEYS.MONSTERS) || [];
  const byMonsterId = new Map(roster.map((m) => [m.monsterId, m]));

  /** @type {EvolutionResult[]} */
  const evolutions = [];

  party.forEach((monster) => {
    // A monster can chain past two thresholds in one pass (e.g. caught/levelled far beyond 36),
    // so loop until it has no further eligible evolution.
    let guard = 0;
    while (guard < 8) {
      guard += 1;
      const evo = evolutionMap[monster.monsterId];
      if (!evo || monster.currentLevel < evo.level) {
        break;
      }
      const target = byMonsterId.get(evo.toId);
      if (!target) {
        break;
      }

      // preserve HP as a ratio of the OLD max so the evolved monster keeps its relative health
      const oldMaxHp = monster.maxHp > 0 ? monster.maxHp : 1;
      const hpRatio = Math.max(0, Math.min(1, monster.currentHp / oldMaxHp));

      const fromName = monster.name;
      const fromAssetKey = monster.assetKey;
      const fromAssetFrame = monster.assetFrame || 0;
      const fromAssetScale = monster.assetScale || 1;

      // replace species fields (identity, sprite, base stats, MOVESET) — keep id / level / exp.
      // The moveset must switch to the EVOLVED species' moves, otherwise the evolved form keeps the
      // pre-evolution moves (e.g. Solanoid showing baby Solani's Tackle/Poison Sting — "looks weird").
      monster.monsterId = target.monsterId;
      monster.name = target.name;
      monster.element = target.element;
      if (Array.isArray(target.attackIds) && target.attackIds.length > 0) {
        monster.attackIds = [...target.attackIds];
      }
      monster.assetKey = target.assetKey;
      monster.assetFrame = target.assetFrame || 0;
      monster.assetScale = target.assetScale || 1;
      // Scale the new species' BASE maxHp up to the monster's current level — otherwise evolution
      // overwrote a level-36 monster's HP with the template's tiny base (e.g. Pyrothar dropping to 51).
      monster.maxHp = hpAtLevel(target.maxHp, monster.currentLevel);
      monster.baseAttack = target.baseAttack;
      monster.baseExp = target.baseExp;
      // keep the monster competitive: never let evolution lower its current attack below the new
      // species' base, but otherwise preserve the levelled-up attack the player earned.
      monster.currentAttack = Math.max(monster.currentAttack || 0, target.baseAttack);
      monster.currentHp = Math.max(1, Math.round(monster.maxHp * hpRatio));

      evolutions.push({
        fromName,
        toName: monster.name,
        fromAssetKey,
        fromAssetFrame,
        fromAssetScale,
        toAssetKey: monster.assetKey,
        toAssetFrame: monster.assetFrame,
        toAssetScale: monster.assetScale,
      });
    }
  });

  if (evolutions.length > 0) {
    dataManager.store.set(DATA_MANAGER_STORE_KEYS.MONSTERS_IN_PARTY, party);
    dataManager.saveData();
  }

  return evolutions;
}
